'use client';
import React, { useEffect, useRef, useState } from 'react';

import { Faq as FaqComponent } from '@monetization/hpaip-components-library';
import { env } from 'next-runtime-env';
import { useTranslation } from 'react-i18next';

import { FlowType } from '@storefront/app/flow-orchestrator/types';
import { useGetFaqsV2 } from '@storefront/hooks/useGetFaqsV2';
import { useIntersection } from '@storefront/hooks/useIntersection';
import { useProductCancellationList } from '@storefront/hooks/useProductCancellationList';
import { NamespacesI18n } from '@storefront/i18n';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { forwardParams } from '@storefront/utils/queryParamsUtils';

import { IFaqType } from './faq.utils';

interface FaqProps {
  skus: string[];
  flowType: FlowType;
  nameSpace?: NamespacesI18n;
  className?: string;
  backgroundColor?: string;
  hasBorder?: boolean;
}

export function Faq({
  skus,
  flowType,
  nameSpace = NamespacesI18n.PDP_CONFIG,
  className,
  backgroundColor,
  hasBorder = false,
}: Readonly<FaqProps>) {
  const [triggerAnalyticsOnce, setTriggerAnalyticsOnce] = useState(true);
  const [configuredFaqQuestion, setConfiguredFaqQuestion] = useState<
    IFaqType[]
  >([]);

  const { t } = useTranslation(nameSpace);
  const { cancellationList } = useProductCancellationList(skus);
  const ref = useRef(null);
  const inViewport = useIntersection(ref, '0px');

  const transitionII = nameSpace === NamespacesI18n.INSTANT_INK;

  const faqs = useGetFaqsV2({
    flowType,
    cancellationList,
    nameSpace,
  });

  useEffect(() => {
    if (configuredFaqQuestion.length <= 0 && faqs.length > 0) {
      setConfiguredFaqQuestion(faqs);
    }
  }, [faqs]);

  useEffect(() => {
    const productAnalytics = transitionII
      ? uiEvents.faqIISD
      : uiEvents.printerConfigFAQSD;
    if (inViewport && triggerAnalyticsOnce) {
      DataCollection.sendEvent(productAnalytics);
      setTriggerAnalyticsOnce(false);
    }
  }, [inViewport]);

  const handleExpand = (_?: unknown, index?: number) => {
    setConfiguredFaqQuestion((state) => {
      const updatedData = [...state];
      updatedData[index ?? 0].expanded = true;
      const productAnalytics = transitionII
        ? uiEvents.faqIIAccordianExpanded
        : uiEvents.printerConfigFAQAccordianExpanded;
      DataCollection.sendEvent({
        ...productAnalytics,
        controlDetail: updatedData[index ?? 0].id,
      });
      return updatedData;
    });
  };

  const handleCollapse = (_?: unknown, index?: number) => {
    if (index !== undefined) {
      setConfiguredFaqQuestion((state) => {
        const updatedData = [...state];
        updatedData[index].expanded = false;
        return updatedData;
      });
    }
  };

  const handleClickFaqs = async () => {
    const faqUrl = env('NEXT_PUBLIC_FULL_FAQ_URL');
    const urlWithParams = forwardParams(faqUrl);

    const productAnalytics = transitionII
      ? uiEvents.faqIIBC
      : uiEvents.printerConfigFAQBC;

    await DataCollection.sendEvent({
      ...productAnalytics,
      controlDetail: urlWithParams,
    });
    window.open(urlWithParams, '_self');
  };

  return (
    <div ref={ref} id="faq-container" className={className}>
      <FaqComponent
        title={t('faqV2.header')}
        handleClick={handleClickFaqs}
        onExpand={handleExpand}
        onCollapse={handleCollapse}
        items={configuredFaqQuestion}
        redirectText={t('faqV2.redirectText')}
        backgroundColor={backgroundColor}
        hasBorder={hasBorder}
      />
    </div>
  );
}

export default Faq;
